import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Filter, Download, X, AlertTriangle } from "lucide-react";
import dayjs from "dayjs";

import ShimmerLoader from "../../hooks/ShimmerLoader";
import Pagination from "../../hooks/Pagination";

export const num = (n) => Number(n || 0).toLocaleString("en-IN");
export const inr = (n) => Number(n || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
export const pct = (n) => (n === null || n === undefined ? "—" : `${n}%`);
export const fmtDate = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—");
export const fmtDateTime = (d) => (d ? dayjs(d).format("DD MMM YYYY, hh:mm A") : "—");
export const customerName = (r) => (r.name || `${r.fname || ""} ${r.lname || ""}`).trim() || "Customer";

/* Static class map so Tailwind can see every class name */
export const ACCENT = {
    amber: "bg-amber-50 text-amber-600",
    green: "bg-green-50 text-green-600",
    slate: "bg-slate-100 text-slate-600",
    rose: "bg-rose-50 text-rose-600",
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
};

export const StatCard = ({ icon: Icon, label, value, sub, accent = "amber", onClick, selected }) => {
    const Tag = onClick ? "button" : "div";
    return (
        <Tag type={onClick ? "button" : undefined} onClick={onClick}
            className={`bg-white rounded-xl shadow p-4 flex items-center gap-3 text-left transition ${onClick ? "hover:shadow-md" : ""} ${selected ? "ring-2 ring-[#f4a406]" : ""}`}>
            <div className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center ${ACCENT[accent]}`}><Icon size={22} /></div>
            <div className="min-w-0">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-2xl font-bold text-gray-800 leading-tight truncate">{value}</p>
                {sub && <p className="text-xs text-gray-400">{sub}</p>}
            </div>
        </Tag>
    );
};

export const ReportHeader = ({ icon: Icon, title, subtitle, activeFilters = 0, showFilters, onToggleFilters, onExport, exporting, exportDisabled }) => (
    <div className="rounded-xl p-4 text-white shadow-lg bg-gradient-to-r from-[#f4a406] to-[#d89005]">
        <div className="flex items-center gap-2 flex-wrap">
            <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center"><Icon size={22} /></div>
            <div>
                <h3 className="text-xl font-bold leading-tight">{title}</h3>
                <p className="text-sm text-white/90">{subtitle}</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
                <button type="button" onClick={onToggleFilters} aria-expanded={showFilters}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors">
                    <Filter size={16} /> {showFilters ? "Hide filters" : "Filters"}
                    {activeFilters > 0 && (
                        <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[#d89005] text-xs font-bold inline-flex items-center justify-center">{activeFilters}</span>
                    )}
                </button>
                <button type="button" onClick={onExport} disabled={exporting || exportDisabled}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-[#d89005] text-sm font-medium hover:bg-white/90 disabled:opacity-60">
                    <Download size={16} /> {exporting ? "Exporting…" : "Export Excel"}
                </button>
            </div>
        </div>
    </div>
);

export const ErrorBanner = ({ message, onRetry }) =>
    message ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex items-center gap-3 text-sm">
            <AlertTriangle size={18} /> <span>{message}</span>
            {onRetry && <button onClick={onRetry} className="ml-auto underline">Retry</button>}
        </div>
    ) : null;

export const FilterActions = ({ onReset }) => (
    <div className="flex justify-end mt-4 space-x-3">
        <button type="button" onClick={onReset} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">Reset</button>
        <button type="submit" className="px-4 py-2 bg-[#f4a406] text-white rounded-md hover:bg-[#d89005]">Apply Filters</button>
    </div>
);

export const inputCls = "w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

/**
 * Paginated drill-down modal fed by a Redux list thunk.
 * props: title, subtitle, listKey (state.performanceReport key), thunk, payload, columns [{header, render(row)}],
 *        onExport, exporting, onClose
 */
export const DrillModal = ({ title, subtitle, listKey, thunk, payload, columns, onExport, exporting, onClose }) => {
    const dispatch = useDispatch();
    const { data, loading, error } = useSelector((s) => s.performanceReport[listKey]);
    const [page, setPage] = useState(1);

    useEffect(() => {
        dispatch(thunk({ perPage: 25, page, filters: payload }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, page, JSON.stringify(payload)]);

    const rows = data?.data || [];
    const total = data?.total || 0;
    const lastPage = data?.last_page || 1;

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3 px-5 py-4 border-b">
                    <div>
                        <h4 className="font-bold text-gray-800">{title}</h4>
                        <p className="text-xs text-gray-500">{subtitle} {!loading && `· ${num(total)} record${total === 1 ? "" : "s"}`}</p>
                    </div>
                    <button onClick={onExport} disabled={exporting || !total}
                        className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#f4a406] text-white text-sm hover:bg-[#d89005] disabled:opacity-50">
                        <Download size={15} /> {exporting ? "Exporting…" : "Export Excel"}
                    </button>
                    <button onClick={onClose} className="p-2 rounded-md hover:bg-gray-100" aria-label="Close"><X size={18} /></button>
                </div>
                <div className="overflow-auto p-5">
                    {error ? (
                        <div className="text-center text-red-600 text-sm py-10">{error}</div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase text-gray-500 border-b">
                                    {columns.map((c) => <th key={c.header} className="py-2 pr-4">{c.header}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {loading && <tr><td colSpan={columns.length}><ShimmerLoader /><ShimmerLoader /><ShimmerLoader /></td></tr>}
                                {!loading && rows.length === 0 && (
                                    <tr><td colSpan={columns.length} className="text-center text-gray-500 py-10">No records found for this selection.</td></tr>
                                )}
                                {!loading && rows.map((r, i) => (
                                    <tr key={`${r.user_id ?? r.redemption_id}-${i}`} className="border-b last:border-0">
                                        {columns.map((c) => <td key={c.header} className="py-2 pr-4">{c.render(r)}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                {lastPage > 1 && (
                    <div className="px-5 py-3 border-t"><Pagination totalPages={lastPage} currentPage={page} setCurrentPage={setPage} /></div>
                )}
            </div>
        </div>
    );
};

/* A count that opens a drill-down; zero stays plain text */
export const CountLink = ({ value, onClick, className = "" }) =>
    Number(value) > 0 ? (
        <button type="button" onClick={onClick} className={`font-semibold underline-offset-2 hover:underline ${className}`}>{num(value)}</button>
    ) : (
        <span className="text-gray-300">0</span>
    );